import type { ReactNode } from 'react'

import { cn } from '@/shared/lib'

type FloatingLabelProps = {
  htmlFor?: string
  /**
   * Raised permanently. Use for controls that always show a value — select,
   * multi-select, textarea — where a centred label would read as content.
   * Leave off for a text input, where the label doubles as the placeholder and
   * only rises once the field is focused or filled.
   */
  isRaised?: boolean
  tone?: keyof typeof TONES
  children: ReactNode
  className?: string
}

const TONES = {
  normal: 'text-muted-foreground',
  error: 'text-destructive',
  success: 'text-success-text',
  disabled: 'text-fg-disabled',
} as const

const BASE =
  'pointer-events-none absolute left-3 z-10 px-1.5 text-label transition-all duration-150 ease-out'

/** Raised: straddling the top border, inside the gap cut by FieldOutline. */
const RAISED = 'top-0 -translate-y-1/2'

/**
 * At rest the label sits centred inside the field with no backdrop; once the
 * field is focused or filled it rises onto the border and takes the canvas
 * colour behind it so the hairline reads as notched rather than crossed out.
 *
 * `:placeholder-shown` drives the transition, so this works uncontrolled and
 * costs no re-render.
 */
const PEER = [
  'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2',
  'peer-placeholder-shown:px-0 peer-placeholder-shown:text-body',
  'peer-focus:top-0 peer-focus:-translate-y-1/2',
  'peer-focus:px-1.5 peer-focus:text-label peer-focus:text-accent-text',
].join(' ')

/**
 * The label of a Verdant field. Note this sits **on** the border rather than
 * inside the field at `top: 6` the way the design project draws it — the
 * inside position crowds the value on a 44px control, and on the border it
 * also frees the full inner height for chips.
 */
export function FloatingLabel({
  htmlFor,
  isRaised = false,
  tone = 'normal',
  children,
  className,
}: FloatingLabelProps) {
  const className_ = cn(BASE, RAISED, !isRaised && PEER, TONES[tone], className)

  // Select and multi-select render their label inside a <button>, where a
  // <label> element is not valid markup — the trigger already names itself
  // through the label text. Combobox does take an `htmlFor`: its field is a
  // real input, which is also why its label floats like a text field's.
  if (htmlFor === undefined) {
    return <span className={className_}>{children}</span>
  }

  return (
    <label htmlFor={htmlFor} className={className_}>
      {children}
    </label>
  )
}
